import React, { useEffect, useCallback, useState, useMemo } from "react"
import { useParams } from "react-router-dom"
import { saveAs } from "file-saver"

// Components
import WithMenu from "../../../components/templates/WithMenu/WithMenu"
import StocksLegacy from "./StocksLegacy"

// Hooks
import useYearsApi from "../../../hooks/Api/useYearsApi"
import useSeasonsApi from "../../../hooks/Api/useSeasonsApi"
import useWeeksApi from "../../../hooks/Api/useWeeksApi"
import useQuotasApi from "../../../hooks/Api/useQuotasApi"
import useStocksApi from "../../../hooks/Api/useStocksApi"
import useAppContext from "../../../hooks/useAppContext"
import usePermissionsState from "../../../hooks/usePermissionsState"

// Constants
import { PERMISSIONS, accTypologyToPermissions } from "../../../constants/constants"

// Utils
import { exportCollectionToArray } from "../../../utils/collection"
import {
  buildWeeksIdsByYears,
  buildOrderedWeeks,
  buildOrderedQuotas,
  buildCollectionWeeks,
  buildActiveYears,
  buildActiveWeeks,
  buildStocks,
  buildQuotasGraph,
} from "../../../utils/yieldStockUtils"

import style from "./Stock.module.css"

const permissions = [
  // camps
  PERMISSIONS.PERM_STOCKS_CAMPS_DISPLAY,
  PERMISSIONS.PERM_STOCKS_CAMPS_EXPORT,
  PERMISSIONS.PERM_STOCKS_CAMPS_UPDATE,

  // extras
  PERMISSIONS.PERM_STOCKS_EXTRAS_DISPLAY,
  PERMISSIONS.PERM_STOCKS_EXTRAS_EXPORT,
  PERMISSIONS.PERM_STOCKS_EXTRAS_UPDATE,

  // accommodations
  PERMISSIONS.PERM_STOCKS_ACCOMMODATIONS_OTHER_UPDATE,
  PERMISSIONS.PERM_STOCKS_ACCOMMODATIONS_OTHER_EXPORT,
  PERMISSIONS.PERM_STOCKS_ACCOMMODATIONS_OTHER_DISPLAY,
  PERMISSIONS.PERM_STOCKS_ACCOMMODATIONS_BOARDING_UPDATE,
  PERMISSIONS.PERM_STOCKS_ACCOMMODATIONS_BOARDING_EXPORT,
  PERMISSIONS.PERM_STOCKS_ACCOMMODATIONS_BOARDING_DISPLAY,
  PERMISSIONS.PERM_STOCKS_ACCOMMODATIONS_RESIDENCE_UPDATE,
  PERMISSIONS.PERM_STOCKS_ACCOMMODATIONS_RESIDENCE_EXPORT,
  PERMISSIONS.PERM_STOCKS_ACCOMMODATIONS_RESIDENCE_DISPLAY,
  PERMISSIONS.PERM_STOCKS_ACCOMMODATIONS_HOTEL_UPDATE,
  PERMISSIONS.PERM_STOCKS_ACCOMMODATIONS_HOTEL_EXPORT,
  PERMISSIONS.PERM_STOCKS_ACCOMMODATIONS_HOTEL_DISPLAY,
]

const Stocks = ({ match }) => {
  const {
    context: { userInfo },
  } = useAppContext()
  const rolePermissions = usePermissionsState(userInfo, permissions)

  const { id: familyId } = useParams()
  const [loading, setLoading] = useState(true)
  const [weeks, setWeeks] = useState({})
  const [exportLoading, setExportLoading] = useState(false)
  const [years, setYears] = useState({})
  const [stocks, setStocks] = useState({})
  const [orderedWeeks, setOrderedWeeks] = useState({})
  const [orderedQuotas, setOrderedQuotas] = useState([])
  const [quotasGraph, setQuotasGraph] = useState({})

  const [, seasonsData, , { getSeasons }] = useSeasonsApi()
  const [, yearsData, , { getYears }] = useYearsApi()
  const [, weeksData, , { getWeeks }] = useWeeksApi()
  const [, quotasData, , { getQuotas }] = useQuotasApi()
  const [, stocksData, , { getStocks }] = useStocksApi()
  const [, , , { getStocksExport }] = useStocksApi()
  const [, , , { updateStocks }] = useStocksApi()

  const savedGetSeasons = useCallback(getSeasons, [])
  const savedGetYears = useCallback(getYears, [])
  const savedGetWeeks = useCallback(getWeeks, [])
  const savedGetQuotas = useCallback(getQuotas, [])
  const savedGetStocks = useCallback(getStocks, [])
  const savedGetStocksExport = useCallback(getStocksExport, [])

  useEffect(() => {
    setLoading(false)
    savedGetSeasons()
    savedGetYears()
    savedGetQuotas({ archived: false })
  }, [savedGetSeasons, savedGetYears, savedGetQuotas, match.params.id])

  useEffect(() => {
    if (userInfo !== null && !loading) {
      setLoading(false)
      savedGetWeeks().then(res => {
        if (res && res.data) {
          const weeksFormatted = exportCollectionToArray(res.data, "weeks")
          const weeksByYears = buildWeeksIdsByYears(weeksFormatted)
          savedGetStocks({
            minWeekId: weeksByYears[0].id,
            maxWeekId: weeksByYears[weeksByYears.length - 1].id,
            familyId,
          }).then(() => setLoading(true))
        }
      })
    }
  }, [familyId, savedGetWeeks, savedGetStocks, userInfo, loading])

  const permsByTypology = useCallback((permType) => {
    const permsByTypology = {};
    Object.keys(accTypologyToPermissions).forEach((typoId) => {
      permsByTypology[typoId] = rolePermissions?.[accTypologyToPermissions[typoId]?.[`${permType}_stocks`]] || false;
    });
    return permsByTypology;
  }, [rolePermissions]);

  useEffect(() => {
    if (weeksData && seasonsData && yearsData && quotasData && stocksData) {
      const yearsFormatted = exportCollectionToArray(yearsData, "years")
      const weeksFormatted = exportCollectionToArray(weeksData, "weeks")
      const quotasFormatted = exportCollectionToArray(quotasData, "quotas")
      const stocksFormatted = exportCollectionToArray(stocksData, "stocks")
      const activeWeeks = buildCollectionWeeks(
        weeksFormatted,
        seasonsData.seasons
      )

      setYears(buildActiveYears(yearsFormatted))
      setWeeks(buildActiveWeeks(activeWeeks))
      setOrderedWeeks(buildOrderedWeeks(activeWeeks, yearsFormatted))
      setOrderedQuotas(buildOrderedQuotas(quotasFormatted, familyId, permsByTypology('display')))
      setQuotasGraph(buildQuotasGraph(quotasFormatted, familyId))
      setStocks(buildStocks(stocksFormatted))
    }
  }, [weeksData, yearsData, seasonsData, quotasData, stocksData, familyId])

  const handleExportStocks = () => {
    setExportLoading(true)
    savedGetStocksExport(familyId)
      .then(res => {
        setExportLoading(false)
        if (res && res.data) saveAs(res.data, "stocks.xlsx")
      })
      .catch(e => {
        console.error(e)
        setExportLoading(false)
      })
  }

  const handleUpdateStocks = stocksEvents =>
    updateStocks(stocksEvents).then(res => {
      if (res && res.data) {
        return res.data
      }
    })

  const getStockSectionName = () => {
    switch (match.params.id) {
      case "1":
        return "Stages"
      case "2":
        return "Extras"
      case "3":
        return "Hébergements"
      default:
        return "Stages"
    }
  }

  const getStockPermissions = () => {
    const campsPerms = {
      authorizedToEdit: !!rolePermissions[PERMISSIONS.PERM_STOCKS_CAMPS_UPDATE],
      authorizedToExport: !!rolePermissions[
        PERMISSIONS.PERM_STOCKS_CAMPS_EXPORT
      ],
    }
    const extrasPerms = {
      authorizedToEdit: !!rolePermissions[
        PERMISSIONS.PERM_STOCKS_EXTRAS_UPDATE
      ],
      authorizedToExport: !!rolePermissions[
        PERMISSIONS.PERM_STOCKS_EXTRAS_EXPORT
      ],
    }

    const updateAccPerms = [
      PERMISSIONS.PERM_STOCKS_ACCOMMODATIONS_OTHER_UPDATE,
      PERMISSIONS.PERM_STOCKS_ACCOMMODATIONS_BOARDING_UPDATE,
      PERMISSIONS.PERM_STOCKS_ACCOMMODATIONS_RESIDENCE_UPDATE,
      PERMISSIONS.PERM_STOCKS_ACCOMMODATIONS_HOTEL_UPDATE
    ];

    const exportAccPerms = [
      PERMISSIONS.PERM_STOCKS_ACCOMMODATIONS_OTHER_EXPORT,
      PERMISSIONS.PERM_STOCKS_ACCOMMODATIONS_BOARDING_EXPORT,
      PERMISSIONS.PERM_STOCKS_ACCOMMODATIONS_RESIDENCE_EXPORT,
      PERMISSIONS.PERM_STOCKS_ACCOMMODATIONS_HOTEL_EXPORT
    ];

    const accommodationsPerms = {
      authorizedToEdit: Object.keys(rolePermissions || {})
        .filter((perm) => updateAccPerms.includes(perm))
        .some((perm) => !!rolePermissions[perm])
      ,
      authorizedToExport: Object.keys(rolePermissions || {})
        .filter((perm) => exportAccPerms.includes(perm))
        .some((perm) => !!rolePermissions[perm])
      ,
    }

    switch (match.params.id) {
      case "1":
        return campsPerms
      case "2":
        return extrasPerms
      case "3":
        return accommodationsPerms
      default:
        return campsPerms
    }
  }

  const filterQuotaOptions = useMemo(() => {
    const options = [];

    if (orderedQuotas?.length) {
      // MACRO QUOTAS
      if (orderedQuotas.some((quotaId) => quotasData?.quotas?.[quotaId]?.macro)) {
        options.push({
          label: 'Macro-Quotas',
          options: orderedQuotas
            .filter((quotaId) => quotasData?.quotas?.[quotaId]?.macro)
            .map((quotaId) => (
              { value: quotaId, label: quotasData.quotas[quotaId].name }
            )),
        });
      }
  
      // PRODUCTS
      options.push({
        label: 'Produits',
        options: orderedQuotas
          .filter((quotaId) => quotasData?.quotas?.[quotaId] && !quotasData.quotas[quotaId].macro)
          .map((quotaId) => (
            { value: quotaId, label: quotasData.quotas[quotaId].name }
          )),
      });
    }

    return options;
  }, [orderedQuotas, quotasData]);

  return (
    <WithMenu>
      <div className={style.pageHeader}>
        <h1>{`Stocks / ${getStockSectionName()}`}</h1>
      </div>

      {years && orderedWeeks && orderedQuotas && loading && userInfo ? (
        <StocksLegacy
          familyId={familyId}
          years={years}
          weeks={weeks}
          quotas={quotasData ? quotasData.quotas : null}
          stocks={stocks}
          orderedYearsIds={Object.keys(years).map(year => year)}
          orderedWeeksIds={orderedWeeks}
          orderedQuotasIds={orderedQuotas}
          quotasGraph={quotasGraph}
          updateStock={handleUpdateStocks}
          exportMatrix={handleExportStocks}
          exportLoading={exportLoading}
          authorizedToEdit={getStockPermissions().authorizedToEdit}
          authorizedToExport={getStockPermissions().authorizedToExport}
          permsByTypology={permsByTypology('update')}
          filterQuotaOptions={filterQuotaOptions}
        />
      ) : (
        <span
          className="spinner-border spinner-border-sm"
          role="status"
          aria-hidden="true"
        />
      )}
    </WithMenu>
  )
}

export default Stocks
